import Component from '../abstracts/Component';

export default class MiniCartComponent extends Component {
  get SELECTORS() {
    return {
      miniCartButton: '[data-minicart-button]',
      miniCartLayer: '[data-minicart-layer]',
      miniCartLayerWrapper: '[data-minicart-layer-wrapper]',
      miniCartContainer: '[data-minicart-container]',
      miniCartCloseButton: '[data-close-minicart-layer]',
      miniCartContent: '[data-minicart-content]',
      miniCartContentNotify: '[data-minicartnotification-content]',
      miniCartCount: '[data-mini-cart-qty-item]',
      // Mini cart HTML identifiers
      removeProductFromMiniCart: '[data-remove-product-from-minicart]',
      miniCartCountIncoming: '[data-minicart-quantity-update]',
      removeProductFromMiniCartAction:
        'data-remove-product-from-minicart-action',
      miniCartItemsWrapper: '[data-mini-cart-items-wrapper]',
    };
  }

  get CLASSES() {
    return {
      miniCartOpenClass: '--open',
      miniCartShowClass: 'show', // Used for XGen mutation observers
      miniCartOpenLayerClass: '--open-layer',

      miniCartOpenClassAfterAdd: '--open-quick',
      miniCartOpenLayerClassAfterAdd: '--open-layer-quick',

      miniCartHiddenClass: '--hidden',
      miniCartCounterHiddenClass: '--hidden',
      miniCartCounterIncomingClass: '--incoming-quantity',
      miniCartCounterOutgoingClass: '--outgoing-quantity',
      miniCartPerformingActionClass: '--performing-action',
    };
  }

  get CONSTANTS() {
    return {
      CART_QUICK_NOTIFICATION_TIMEOUT: 2500,
    };
  }

  constructor(htmlEl) {
    super(htmlEl);
    this.currentViewport = null;
    this.timeOutQuickShow = null;

    this.dom = {
      miniCartButton: this.$component.querySelector(
        this.SELECTORS.miniCartButton
      ),
      miniCartCloseButton: this.$component.querySelector(
        this.SELECTORS.miniCartCloseButton
      ),
      miniCartLayer: this.$component.querySelector(
        this.SELECTORS.miniCartLayer
      ),
      miniCartLayerWrapper: this.$component.querySelector(
        this.SELECTORS.miniCartLayerWrapper
      ),
      miniCartContainer: document.querySelector(
        this.SELECTORS.miniCartContainer
      ),
      miniCartContent: this.$component.querySelector(
        this.SELECTORS.miniCartContent
      ),
      miniCartCount: this.$component.querySelector(
        this.SELECTORS.miniCartCount
      ),
      miniCartContentNotify: this.$component.querySelector(
        this.SELECTORS.miniCartContentNotify
      ),
      headerElement: document.querySelector('header.c-main-header'),
    };
  }

  /**
   * This function is called before show the mini cart layer in order to calculate the max height
   * based on the current height of the header, to prevent hiding the layer when it's greaten
   * than the viewport.
   */
  updateMiniCartMaxHeight() {
    if (
      this.dom.miniCartLayerWrapper &&
      this.dom.headerElement &&
      this.currentViewport !== 'mobile'
    ) {
      this.dom.miniCartLayerWrapper.style.maxHeight = `calc(100dvh - ${this.dom.headerElement.offsetHeight}px)`;
    }
  }

  async removeFromMiniCart(miniCartProductAction) {
    const url = miniCartProductAction;
    this.dom.miniCartContent.classList.add(
      this.CLASSES.miniCartPerformingActionClass
    );
    await fetch(url, {
      method: 'POST',
    })
      .then((response) => {
        if (response?.status === 200) {
          this.updateMiniCart();
        }
      })
      .catch((error) => {
        console.error(error);
        this.dom.miniCartContent.classList.remove(
          this.CLASSES.miniCartPerformingActionClass
        );
      });
  }

  updateMiniCartQuantity(newCartQty) {
    // Identify the quantity container and check if exists
    const parentElement = this.dom.miniCartCount;
    if (!parentElement) {
      console.error('Quantity element not found.');
      return;
    }
    // If the quantity of elements is greaten than 0, proceed
    if (newCartQty > 0) {
      if (
        parentElement.classList.contains(
          this.CLASSES.miniCartCounterHiddenClass
        )
      )
        parentElement.classList.remove(this.CLASSES.miniCartCounterHiddenClass);
      // Get first child of quantity container and check if exists
      const firstChild = parentElement.firstElementChild;
      if (!firstChild) {
        console.error('Quantity element has no children.');
        return;
      }
      // Clone current quantity child and apply next quantity number
      const clonedChild = firstChild.cloneNode(true);
      clonedChild.innerHTML = newCartQty;
      // Add corresponding animations classes
      clonedChild.classList.add(this.CLASSES.miniCartCounterIncomingClass);
      firstChild.classList.add(this.CLASSES.miniCartCounterOutgoingClass);
      // Inject new child with updated quantity
      parentElement.insertBefore(clonedChild, firstChild.nextSibling);
      // Delay the deletion of the first quantity child
      setTimeout(() => {
        parentElement.removeChild(firstChild);
      }, 300);
    } else if (
      !parentElement.classList.contains(this.CLASSES.miniCartCounterHiddenClass)
    )
      parentElement.classList.add(this.CLASSES.miniCartCounterHiddenClass);
  }

  async updateMiniCart() {
    const url = this.dom.miniCartButton.dataset.urlMiniCart;
    if (
      !this.dom.miniCartContent.classList.contains(
        this.CLASSES.miniCartPerformingActionClass
      )
    )
      this.dom.miniCartContent.classList.add(
        this.CLASSES.miniCartPerformingActionClass
      );
    this.dom.miniCartContent.classList.add(
      this.CLASSES.miniCartPerformingActionClass
    );
    /**
     * Refresh the current mini cart by getting the HTML content from fetch request
     */
    await fetch(url)
      .then((response) => {
        return response.text();
      })
      .then((text) => {
        try {
          /**
           * Check the current mini cart HTML in order to identify the current
           * "Remove from cart" buttons and delete the click events listeners
           * in order to prevent memory leaks and performance issues
           */
          const currentMiniCartProductRemoveButtons =
            this.dom.miniCartContent.querySelectorAll(
              this.SELECTORS.removeProductFromMiniCart
            );
          if (currentMiniCartProductRemoveButtons.length)
            currentMiniCartProductRemoveButtons.forEach(
              (currentRemoveButton) => {
                currentRemoveButton.removeEventListener('click', () =>
                  this.removeFromMiniCart(
                    currentRemoveButton.getAttribute(
                      this.SELECTORS.removeProductFromMiniCartAction
                    )
                  )
                );
              }
            );
          /**
           * Convert the new response as HTML
           */
          const htmlResponseMiniCart = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          /**
           * Identify all "Remove from cart" buttons for each product in the response
           */
          const newMiniCartProductRemoveButtons =
            htmlResponseMiniCart.body.querySelectorAll(
              this.SELECTORS.removeProductFromMiniCart
            );
          /**
           * Add click event listeners in the new "Remove from card" buttons
           */
          if (newMiniCartProductRemoveButtons.length)
            newMiniCartProductRemoveButtons.forEach((currentRemoveButton) => {
              currentRemoveButton.addEventListener('click', () =>
                this.removeFromMiniCart(
                  currentRemoveButton.getAttribute(
                    this.SELECTORS.removeProductFromMiniCartAction
                  )
                )
              );
            });
          /**
           * Retrieve the new mini cart count from data set in incoming HTML
           */
          const newMiniCartQuantity =
            htmlResponseMiniCart.body.querySelector(
              this.SELECTORS.miniCartCountIncoming
            ).innerHTML || null;
          /**
           * Clean the current mini cart HTML content and append the new content
           */
          this.dom.miniCartContent.innerHTML = '';
          while (htmlResponseMiniCart.body.firstChild) {
            this.dom.miniCartContent.append(
              htmlResponseMiniCart.body.firstChild
            );
          }
          this.updateMiniCartQuantity(newMiniCartQuantity);
        } catch (error) {
          console.error('Error parsing result while updating the cart.');
          console.error(error);
        }
      })
      .catch((error) => console.error(error))
      .finally(() =>
        this.dom.miniCartContent.classList.remove(
          this.CLASSES.miniCartPerformingActionClass
        )
      );
  }

  cancelQuickCartNotification() {
    this.dom.miniCartContainer.classList.remove(
      this.CLASSES.miniCartOpenLayerClassAfterAdd,
      this.CLASSES.miniCartShowClass
    );
    this.dom.miniCartLayer.classList.remove(
      this.CLASSES.miniCartOpenClassAfterAdd
    );
  }

  openQuickCartNotification() {
    if (this.currentViewport !== 'mobile') {
      this.dom.miniCartContainer.classList.add(
        this.CLASSES.miniCartOpenLayerClassAfterAdd,
        this.CLASSES.miniCartShowClass
      );
      this.updateMiniCartMaxHeight();
      this.dom.miniCartLayer.classList.add(
        this.CLASSES.miniCartOpenClassAfterAdd
      );
      this.timeOutQuickShow = setTimeout(() => {
        this.cancelQuickCartNotification();
      }, this.CONSTANTS.CART_QUICK_NOTIFICATION_TIMEOUT);
    }
  }

  closeMiniCart() {
    if (
      this.dom.miniCartContainer.classList.contains(
        this.CLASSES.miniCartOpenLayerClass
      )
    ) {
      this.dom.miniCartLayer.classList.remove(this.CLASSES.miniCartOpenClass);
      this.dom.miniCartContainer.classList.remove(
        this.CLASSES.miniCartOpenLayerClass,
        this.CLASSES.miniCartShowClass
      );
      if (this.currentViewport === 'mobile') {
        this.$emit(this.$customEvents.BENEFIT_BAR_EVENTS.show);
      }
    }
  }

  handleCartButtonClick(event) {
    clearTimeout(this.timeOutQuickShow);
    this.cancelQuickCartNotification();
    if (this.currentViewport === 'tablet') {
      event.preventDefault();
      if (
        this.dom.miniCartLayer.classList.contains(
          this.CLASSES.miniCartOpenClass
        )
      ) {
        this.$emit(this.$customEvents.BACKDROP_EVENTS.hide);
        this.closeMiniCart();
      } else {
        this.$emit(this.$customEvents.BACKDROP_EVENTS.show, {
          disableGlobalOverflow: true,
        });
        this.$emit(this.$customEvents.MINICART_EVENTS.open);
        const miniCartItemsWrapper = this.dom.miniCartLayer.querySelector(
          this.SELECTORS.miniCartItemsWrapper
        );
        this.dom.miniCartContent.scrollTop = 0;
        this.dom.miniCartLayerWrapper.scrollTop = 0;
        if (miniCartItemsWrapper) miniCartItemsWrapper.scrollTop = 0;
        this.updateMiniCartMaxHeight();
        this.dom.miniCartLayer.classList.add(this.CLASSES.miniCartOpenClass);
        /**
         * Hide benefit bar, navigation menu and search layer
         */
        this.$emit(this.$customEvents.MENU_EVENTS.hideMenu, {
          fromMiniCart: true,
          showBenefitBar: this.currentViewport === 'tablet',
        });
        this.$emit(this.$customEvents.SEARCH_LAYER_EVENTS.hide);
        this.$emit(this.$customEvents.MINIWISHLIST_EVENTS.close);
        if (this.currentViewport === 'mobile') {
          this.$emit(this.$customEvents.BENEFIT_BAR_EVENTS.hide);
        }
        this.dom.miniCartContainer.classList.add(
          this.CLASSES.miniCartOpenLayerClass,
          this.CLASSES.miniCartShowClass
        );
      }
    }
  }

  calculateCurrentViewport() {
    const viewportWidth = window.innerWidth;
    let currentViewport = 'mobile';
    if (viewportWidth >= 768 && viewportWidth < 1440) {
      currentViewport = 'tablet';
    } else if (viewportWidth >= 1440) {
      currentViewport = 'desktop';
    }

    return currentViewport;
  }

  bindEvents() {
    // Detect the current viewport
    this.currentViewport = this.calculateCurrentViewport();
    window.addEventListener('resize', () => {
      const newViewport = this.calculateCurrentViewport();
      if (this.currentViewport !== newViewport) {
        if (newViewport === 'mobile') {
          clearTimeout(this.timeOutQuickShow);
          this.cancelQuickCartNotification();
        }
        if (
          this.dom.miniCartLayer.classList.contains(
            this.CLASSES.miniCartOpenClass
          )
        ) {
          if (newViewport === 'mobile') {
            this.$emit(this.$customEvents.BENEFIT_BAR_EVENTS.hide);
          } else {
            this.$emit(this.$customEvents.BENEFIT_BAR_EVENTS.show);
            if (newViewport === 'desktop') {
              this.dom.miniCartLayer.classList.remove(
                this.CLASSES.miniCartOpenClass
              );
            }
          }
        }
      }
      this.currentViewport = newViewport;
    });

    // On item added to cart, show quick cart notification and update mini cart
    this.$on(this.$customEvents.PRODUCT_EVENTS.addedtocart, () => {
      this.openQuickCartNotification();
      this.updateMiniCart();
    });

    // Bind the mini cart button mouseenter to calculate the max height of the minicart layout
    this.dom.miniCartButton.addEventListener('mouseenter', () =>
      this.updateMiniCartMaxHeight()
    );

    // Bind the mini cart button click to open the minicart layout
    this.dom.miniCartButton.addEventListener('click', (event) =>
      this.handleCartButtonClick(event)
    );

    // Bind the mini cart button when mouse enter in desktop
    this.dom.miniCartContainer.addEventListener('mouseenter', () => {
      if (this.currentViewport === 'desktop') {
        clearTimeout(this.timeOutQuickShow);
        this.cancelQuickCartNotification();
        const miniCartItemsWrapper = this.dom.miniCartLayer.querySelector(
          this.SELECTORS.miniCartItemsWrapper
        );
        if (miniCartItemsWrapper) miniCartItemsWrapper.scrollTop = 0;
        this.dom.miniCartLayerWrapper.scrollTop = 0;
        this.dom.miniCartContainer.classList.add(
          this.CLASSES.miniCartShowClass
        );
      }
    });

    // Bind the mini cart button when mouse leave in desktop
    this.dom.miniCartContainer.addEventListener('mouseleave', () => {
      if (this.currentViewport === 'desktop') {
        this.dom.miniCartContainer.classList.remove(
          this.CLASSES.miniCartShowClass
        );
      }
    });

    // Bind the mini cart close button to close the minicart layout
    this.dom.miniCartCloseButton.addEventListener('click', () => {
      this.$emit(this.$customEvents.BENEFIT_BAR_EVENTS.show);
      this.$emit(this.$customEvents.BACKDROP_EVENTS.hide);
      this.dom.miniCartLayer.classList.remove(this.CLASSES.miniCartOpenClass);
      this.dom.miniCartContainer.classList.remove(
        this.CLASSES.miniCartOpenLayerClass,
        this.CLASSES.miniCartShowClass
      );
    });

    // Listen to backdrop close event
    this.$on(this.$customEvents.BACKDROP_EVENTS.close, () => {
      this.closeMiniCart();
    });

    // Listen to mini cart close, if current layout is not desktop
    this.$on(this.$customEvents.MINICART_EVENTS.close, () => {
      if (this.currentViewport !== 'desktop') this.closeMiniCart();
    });

    // Listen to mini cart update event
    this.$on(this.$customEvents.MINICART_EVENTS.update, (event) => {
      if (event?.payload?.quickNotify === true)
        this.openQuickCartNotification();
      this.updateMiniCart();
    });
  }

  render() {
    this.log('Rendering...');
    // on page load retrieve the mini cart content
    this.updateMiniCart().then(() => {
      this.bindEvents();
    });
  }
}
